<script setup lang="ts">
  import { getGetCustomerQueryKey } from "@/api/customer/customer.ts"
  import { MyAccountRoute } from "@/constants/routes/myAccount.ts"
  import TrackingService from "@/services/TrackingService.ts"
  import { useQueryClient } from "@tanstack/vue-query"
  import { onMounted } from "vue"
  import { useRouter } from "vue-router"
  import { Button } from "@/components/ui/button"

  const queryClient = useQueryClient()
  const router = useRouter()

  onMounted(() => {
    TrackingService.sendEvent("account_load_error_view")
  })

  /**
   * The failed customer query stays cached with its error, and useGetAccountId
   * redirects here on sight of one. Navigating home without clearing it lands
   * straight back on this page, so reset the query first and let home fetch it
   * afresh.
   */
  const retry = async () => {
    await queryClient.resetQueries({ queryKey: getGetCustomerQueryKey() })
    await router.push(MyAccountRoute.home)
  }
</script>

<template>
  <div class="flex w-full flex-col items-center justify-center">
    <img
      src="@/assets/illustrations/accountNotLoaded.svg"
      alt="Account not loaded"
      class="h-50 mt-18"
    />
    <div class="max-w-[600px] items-center space-y-10 py-10 text-center">
      <h1 class="text-4xl font-medium" data-testid="account-load-error-title">
        Sorry, we were unable to load your account.
      </h1>
      <p>
        We couldn't load your account at this time, click the button below to
        try again.
      </p>
      <p>
        If you're still unable to view your account, we're on it. We're
        resolving a known issue and recommend trying again later today.
      </p>
      <Button data-testid="account-load-error-button" @click="retry">
        Try again
      </Button>
    </div>
  </div>
</template>
